import { formatPlaceTitle } from '@/utils/formatPlaceTitle';

/**
 * CP-34: Browse destination filter options must be canonical {id, label} only.
 * Never render raw synonym strings (e.g. "Western Caribbean") as chips.
 * Stale Redis payloads from pre-CP-34 /filters/all returned plain strings —
 * drop those so the UI cannot reintroduce near-duplicate chips.
 */
export function normalizeCanonicalDestinations(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const id = typeof item.id === 'string' ? item.id.trim() : '';
    const label = typeof item.label === 'string' ? item.label.trim() : '';
    if (!id || !label) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: formatPlaceTitle(label) });
  }
  return out;
}

/**
 * Consumer label for a canonical destination id.
 * Compare stores the id on the select (that is the API filter value) and
 * must print the label in the heading, not the slug.
 */
export function labelForDestinationId(destinations, id) {
  if (id == null || id === '') return '';
  const key = String(id);
  if (!Array.isArray(destinations)) return key;
  for (const dest of destinations) {
    if (dest && typeof dest === 'object') {
      if (dest.id !== key) continue;
      const label = typeof dest.label === 'string' ? dest.label.trim() : '';
      return label ? formatPlaceTitle(label) : key;
    }
    if (dest === key) return key;
  }
  return key;
}

/**
 * Build canonical chip options from enriched cruise rows (fallback when
 * /filters/all is empty or still serving legacy raw strings).
 * Unmapped sailings are omitted — they must not create synonym chips.
 */
export function destinationsFromCruises(cruises) {
  if (!Array.isArray(cruises)) return [];
  const byId = new Map();
  for (const c of cruises) {
    if (!c || !c.destination_id || !c.destination) continue;
    if (c.destination_unmapped) continue;
    if (!byId.has(c.destination_id)) {
      byId.set(c.destination_id, {
        id: c.destination_id,
        label: formatPlaceTitle(c.destination),
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
}
